import React, { useState } from 'react'

const Faq = () => {
    const [selectedType, setSelectedType] = useState('families')

    const familiesContent = {
        step1: {
            title: "Search for Tutors",
            description: "Browse a range of independent tutors and filter by subject, experience, and teaching style to find the right fit for your learning needs.",
            badge: "Browse"
        },
        step2: {
            title: "Book a Session",
            description: "Book individual sessions or set up a recurring schedule that fits around your routine, with simple tools to manage bookings and availability.",
            badge: "Schedule"
        },
        step3: {
            title: "Start Learning",
            description: "Begin sessions directly with your chosen tutor, focusing on consistent support and meaningful progress.",
            badge: "Commence"
        }
    }

    const tutorsContent = {
        step1: {
            title: "List Your Services",
            description: "Set a profile that reflects your experience, subjects, and availability, giving families a clear picture of how you work.",
            badge: "Create"
        },
        step2: {
            title: "Accept Bookings",
            description: "Choose which enquiries to accept and manage your schedule in a way that works for you.",
            badge: "Connect"
        },
        step3: {
            title: "Teach with Confidence",
            description: "Deliver tutoring sessions independently, maintaining your autonomy while connecting with families through the platform.",
            badge: "Begin"
        }
    }

    const currentContent = selectedType === 'families' ? familiesContent : tutorsContent

    return (
        <main className="max-w-[960px] mx-auto px-4 py-12">
            {/* Hero Section */}
            <div className="@container mb-12">
                <div
                    className="flex min-h-[360px] flex-col gap-6 bg-cover bg-center bg-no-repeat @[480px]:gap-8 rounded-xl items-center justify-center p-8 relative overflow-hidden"
                    data-alt="Group of students studying together in a modern library"
                    style={{
                        backgroundImage:
                            'linear-gradient(rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.7) 100%), url("/assets/faq.png")'
                    }}
                >
                    <div className="flex flex-col gap-4 text-center z-10 max-w-2xl">
                        <h1 className="text-white text-4xl font-black leading-tight tracking-[-0.033em] @[480px]:text-5xl">
                            How the Platform Works
                        </h1>
                        <p className="text-white/90 text-sm font-normal leading-normal @[480px]:text-lg">
                            A simple, transparent way to browse tutors, make a choice, and get started with confidence.
                        </p>
                    </div>
                </div>
            </div>
            {/* Toggle Section */}
            <div className="flex justify-center mb-12">
                <div className="flex h-12 w-full max-w-md items-center justify-center rounded-xl bg-[#e7f3ee] dark:bg-primary/10 p-1">
                    <label className={`flex cursor-pointer h-full grow items-center justify-center overflow-hidden rounded-lg px-2 text-sm font-bold transition-all ${selectedType === 'families'
                        ? 'bg-white dark:bg-primary shadow-sm text-[#0d1b15] dark:text-white'
                        : 'text-[#4c9a78]'
                        }`}>
                        <span className="truncate">For Families</span>
                        <input
                            className="invisible w-0"
                            name="user-type"
                            type="radio"
                            value="families"
                            checked={selectedType === 'families'}
                            onChange={(e) => setSelectedType(e.target.value)}
                        />
                    </label>
                    <label className={`flex cursor-pointer h-full grow items-center justify-center overflow-hidden rounded-lg px-2 text-sm font-bold transition-all ${selectedType === 'tutors'
                        ? 'bg-white dark:bg-primary shadow-sm text-[#0d1b15] dark:text-white'
                        : 'text-[#4c9a78]'
                        }`}>
                        <span className="truncate">For Tutors</span>
                        <input
                            className="invisible w-0"
                            name="user-type"
                            type="radio"
                            value="tutors"
                            checked={selectedType === 'tutors'}
                            onChange={(e) => setSelectedType(e.target.value)}
                        />
                    </label>
                </div>
            </div>
            {/* Step-by-Step Flow */}
            <div className="space-y-16 py-8">
                <h2 className="text-2xl font-black text-center mb-12 tracking-tight">
                    Your Journey to Success
                </h2>
                {/* Step 1 */}
                <div className="grid grid-cols-1 md:grid-cols-[40px_1fr] gap-x-8 items-start group">
                    <div className="hidden md:flex flex-col items-center gap-4 pt-1">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-black border-2 border-primary">
                            1
                        </div>
                        <div className="w-[2px] bg-primary/20 h-32 grow group-last:hidden" />
                    </div>
                    <div className="bg-white dark:bg-primary/5 p-8 rounded-xl border border-[#e7f3ee] dark:border-white/5 flex flex-col md:flex-row gap-8 items-center shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex-1">
                            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-4 uppercase tracking-wider">
                                {currentContent.step1.badge}
                            </span>
                            <h3 className="text-xl font-bold mb-3">{currentContent.step1.title}</h3>
                            <p className="text-[#4c9a78] dark:text-[#a0c4b4] text-base leading-relaxed">
                                {currentContent.step1.description}
                            </p>
                        </div>
                        <div className="w-24 h-24 flex items-center justify-center bg-[#f8fcfa] dark:bg-white/5 rounded-2xl">
                            <span className="material-symbols-outlined text-primary !text-5xl">
                                search
                            </span>
                        </div>
                    </div>
                </div>
                {/* Step 2 */}
                <div className="grid grid-cols-1 md:grid-cols-[40px_1fr] gap-x-8 items-start group">
                    <div className="hidden md:flex flex-col items-center gap-4 pt-1">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-black border-2 border-primary">
                            2
                        </div>
                        <div className="w-[2px] bg-primary/20 h-32 grow group-last:hidden" />
                    </div>
                    <div className="bg-white dark:bg-primary/5 p-8 rounded-xl border border-[#e7f3ee] dark:border-white/5 flex flex-col md:flex-row-reverse gap-8 items-center shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex-1">
                            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-4 uppercase tracking-wider">
                                {currentContent.step2.badge}
                            </span>
                            <h3 className="text-xl font-bold mb-3">{currentContent.step2.title}</h3>
                            <p className="text-[#4c9a78] dark:text-[#a0c4b4] text-base leading-relaxed">
                                {currentContent.step2.description}
                            </p>
                        </div>
                        <div className="w-24 h-24 flex items-center justify-center bg-[#f8fcfa] dark:bg-white/5 rounded-2xl">
                            <span className="material-symbols-outlined text-primary !text-5xl">
                                calendar_today
                            </span>
                        </div>
                    </div>
                </div>
                {/* Step 3 */}
                <div className="grid grid-cols-1 md:grid-cols-[40px_1fr] gap-x-8 items-start group">
                    <div className="hidden md:flex flex-col items-center gap-4 pt-1">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-black border-2 border-primary">
                            3
                        </div>
                        <div className="w-[2px] bg-primary/20 h-16 grow group-last:hidden" />
                    </div>
                    <div className="bg-white dark:bg-primary/5 p-8 rounded-xl border border-[#e7f3ee] dark:border-white/5 flex flex-col md:flex-row gap-8 items-center shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex-1">
                            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-4 uppercase tracking-wider">
                                {currentContent.step3.badge}
                            </span>
                            <h3 className="text-xl font-bold mb-3">{currentContent.step3.title}</h3>
                            <p className="text-[#4c9a78] dark:text-[#a0c4b4] text-base leading-relaxed">
                                {currentContent.step3.description}
                            </p>
                        </div>
                        <div className="w-24 h-24 flex items-center justify-center bg-[#f8fcfa] dark:bg-white/5 rounded-2xl">
                            <span className="material-symbols-outlined text-primary !text-5xl">
                                school
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {/* Trust Section */}
            <div className="bg-primary/5 rounded-2xl p-8 my-20 border border-primary/10">
                <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold mb-2">Built on Trust</h3>
                    <p className="text-sm text-[#4c9a78]">
                        Designed to support safe, transparent tutoring.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center space-y-2">
                        <span className="material-symbols-outlined text-primary">
                            verified_user
                        </span>
                        <h4 className="font-bold text-sm">Verified Credentials</h4>
                        <p className="text-xs text-[#4c9a78]">
                            Tutors complete identity and permit checks to support a trusted learning environment.
                        </p>
                    </div>
                    <div className="text-center space-y-2">
                        <span className="material-symbols-outlined text-primary">lock</span>
                        <h4 className="font-bold text-sm">Secure Payments</h4>
                        <p className="text-xs text-[#4c9a78]">
                            Payments are handled securely, with clear processes in place for sessions and billing.
                        </p>
                    </div>
                    <div className="text-center space-y-2">
                        <span className="material-symbols-outlined text-primary">forum</span>
                        <h4 className="font-bold text-sm">Ongoing Support</h4>
                        <p className="text-xs text-[#4c9a78]">
                            Our team is available to support families and tutors when help is needed.
                        </p>
                    </div>
                </div>
            </div>
            {/* Final CTA */}
            <div className="flex flex-col items-center text-center gap-6 py-12">
                <h2 className="text-3xl font-black max-w-lg">
                    Ready to find the right tutor?
                </h2>
                <div className="flex flex-wrap justify-center gap-4">
                    <button className="flex min-w-[200px] cursor-pointer items-center justify-center rounded-lg h-12 px-6 bg-primary text-white text-base font-bold tracking-[0.015em] hover:bg-opacity-90 transition-all">
                        Get Started for Free
                    </button>
                    <button className="flex min-w-[200px] cursor-pointer items-center justify-center rounded-lg h-12 px-6 bg-[#e7f3ee] dark:bg-white/10 text-primary dark:text-white text-base font-bold tracking-[0.015em] hover:bg-primary/20 transition-all">
                        Get in touch
                    </button>
                </div>
                <p className="text-sm text-[#4c9a78]">
                    No payment is taken until a session is booked.
                </p>
            </div>
        </main>
    )
}

export default Faq;